import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Shuffle,
  Check,
  Repeat,
  RotateCcw,
  Layers,
  Sparkles,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { FLASHCARD_BY_ID } from "@/data/flashcards";

const KNOWN_KEY = (id) => `certivsims.flashcards.known.${id}`;

/* Study a single deck. Behaviour:
   - Cards are drawn from the "study pool" (not yet marked Got it).
   - "Got it" → adds the card id to the known set (persisted to localStorage)
     and removes it from this session's rotation. Auto-advances to next.
   - "Study again" → skips forward without adding to known. Keeps in rotation.
   - Shuffle → reorders the remaining study pool.
   - Restart deck → clears the known set for this deck (both memory + storage).
   - When the study pool is empty → shows a "you've cleared the deck" screen
     with restart / go-back CTAs.
*/
export default function FlashcardsDeck() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const category = FLASHCARD_BY_ID[categoryId];

  // Load persisted "known" set once on mount.
  const [known, setKnown] = useState(() => {
    if (!category) return new Set();
    try {
      const raw = localStorage.getItem(KNOWN_KEY(categoryId));
      if (raw) return new Set(JSON.parse(raw));
    } catch {
      /* corrupted — start fresh */
    }
    return new Set();
  });

  // The study pool is the ordered list of cards NOT yet marked known.
  // We keep an internal order (initially original, later shuffled) so
  // "Shuffle" is a distinct user action rather than an implicit re-order.
  const [order, setOrder] = useState(() =>
    category ? category.cards.map((c) => c.id) : [],
  );
  const [cursor, setCursor] = useState(0);
  const [flipped, setFlipped] = useState(false);

  // Persist known set whenever it changes.
  useEffect(() => {
    if (!category) return;
    try {
      localStorage.setItem(KNOWN_KEY(categoryId), JSON.stringify([...known]));
    } catch {
      /* private-mode browsers etc. — ignore */
    }
  }, [known, categoryId, category]);

  const studyPool = useMemo(
    () =>
      category
        ? order.filter((id) => !known.has(id))
        : [],
    [order, known, category],
  );

  // If the cursor now points past the end of the pool, snap it back to 0.
  useEffect(() => {
    if (cursor >= studyPool.length && studyPool.length > 0) setCursor(0);
  }, [cursor, studyPool.length]);

  const currentCard = category?.cards.find(
    (c) => c.id === studyPool[cursor],
  );

  // Reset flip whenever the CARD showing changes — either because we advanced
  // the cursor OR because a "Got it" filtered the old card out of the pool
  // (cursor stays put but the visible card changes). Watching cursor alone
  // misses the second case and leaves the next card showing its answer face.
  useEffect(() => setFlipped(false), [currentCard?.id]);

  const advance = useCallback(() => {
    setCursor((c) => (studyPool.length > 1 ? (c + 1) % studyPool.length : 0));
  }, [studyPool.length]);

  const markKnown = useCallback(() => {
    if (!currentCard) return;
    setKnown((prev) => {
      const next = new Set(prev);
      next.add(currentCard.id);
      return next;
    });
    // don't advance — the filter will drop this card and cursor stays put
    // to reveal the next one in the same slot
  }, [currentCard]);

  const studyAgain = useCallback(() => advance(), [advance]);

  const shuffle = useCallback(() => {
    setOrder((prev) => {
      const next = [...prev];
      for (let i = next.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [next[i], next[j]] = [next[j], next[i]];
      }
      return next;
    });
    setCursor(0);
    setFlipped(false);
  }, []);

  const restart = useCallback(() => {
    setKnown(new Set());
    try {
      localStorage.removeItem(KNOWN_KEY(categoryId));
    } catch {
      /* ignore */
    }
    setCursor(0);
    setFlipped(false);
  }, [categoryId]);

  if (!category) {
    return (
      <div className="min-h-screen bg-[#F4F7F9] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900">Deck not found</h1>
          <p className="mt-2 text-slate-600">This flashcard category doesn't exist.</p>
          <Link
            to="/flashcards"
            data-testid="flashcards-notfound-back"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#0047AB] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#003a8f]"
          >
            <ArrowLeft className="w-4 h-4" /> All decks
          </Link>
        </div>
      </div>
    );
  }

  const totalCards = category.cards.length;
  const knownCount = known.size;
  const pct = Math.round((knownCount / totalCards) * 100);
  const deckCleared = studyPool.length === 0;
  const Icon = category.Icon;

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/sections" className="flex items-center gap-3" data-testid="brand-logo-home">
            <BrandLogo chip />
          </Link>
          <button
            type="button"
            onClick={() => navigate("/flashcards")}
            data-testid="flashcards-deck-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0047AB]"
          >
            <ArrowLeft className="w-4 h-4" /> All decks
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10" data-testid={`flashcards-deck-${categoryId}`}>
        {/* deck header */}
        <div className="flex items-start gap-3 mb-4">
          <div
            className="w-11 h-11 rounded-lg text-white flex items-center justify-center shrink-0"
            style={{ background: category.accent }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-[11px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: category.accent }}
            >
              Deck {String(category.n).padStart(2, "0")}
            </p>
            <h1 className="mt-0.5 text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">
              {category.title}
            </h1>
          </div>
        </div>

        {/* progress row */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-slate-700">
              <Layers className="w-4 h-4 text-slate-400" />
              <span data-testid="flashcards-progress-total">{totalCards}</span> cards
            </span>
            <span className="inline-flex items-center gap-1.5 text-emerald-700">
              <Check className="w-4 h-4" />
              <span data-testid="flashcards-progress-known">{knownCount}</span> known
            </span>
            <span className="inline-flex items-center gap-1.5 text-slate-700">
              <Sparkles className="w-4 h-4 text-slate-400" />
              <span data-testid="flashcards-progress-remaining">{studyPool.length}</span> to study
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={shuffle}
              disabled={studyPool.length < 2}
              data-testid="flashcards-shuffle-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Shuffle className="w-3.5 h-3.5" /> Shuffle
            </button>
            <button
              type="button"
              onClick={restart}
              disabled={knownCount === 0}
              data-testid="flashcards-restart-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Restart deck
            </button>
          </div>
        </div>

        {/* progress bar */}
        <div className="mt-3 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: category.accent }}
            data-testid="flashcards-progress-bar"
          />
        </div>

        {/* card area */}
        <div className="mt-8">
          {deckCleared ? (
            <DeckClearedView category={category} onRestart={restart} />
          ) : (
            <>
              <CardStage
                key={currentCard.id}
                card={currentCard}
                flipped={flipped}
                accent={category.accent}
                onFlip={() => setFlipped((f) => !f)}
                position={cursor + 1}
                poolSize={studyPool.length}
              />
              <div className="mt-6 grid grid-cols-2 gap-3 sm:flex sm:items-center sm:justify-center">
                <button
                  type="button"
                  onClick={studyAgain}
                  data-testid="flashcards-studyagain-btn"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Repeat className="w-4 h-4" /> Study again
                </button>
                <button
                  type="button"
                  onClick={markKnown}
                  data-testid="flashcards-gotit-btn"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-500"
                >
                  <Check className="w-4 h-4" /> Got it
                </button>
              </div>
              <p className="mt-4 text-center text-xs text-slate-500">
                Click the card to flip it. Progress saves automatically in this browser.
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function CardStage({ card, flipped, accent, onFlip, position, poolSize }) {
  return (
    <div className="[perspective:1200px]">
      <button
        type="button"
        onClick={onFlip}
        data-testid="flashcards-card"
        aria-pressed={flipped}
        className="relative w-full h-[300px] sm:h-[340px] rounded-2xl transition-transform duration-500 [transform-style:preserve-3d] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0047AB]"
        style={{ transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)" }}
      >
        {/* front */}
        <div
          className="absolute inset-0 rounded-2xl bg-white border border-slate-200 shadow-sm flex flex-col items-stretch [backface-visibility:hidden]"
          style={{ borderTopWidth: 3, borderTopColor: accent }}
          aria-hidden={flipped}
        >
          <div className="flex items-center justify-between px-5 pt-4">
            <span
              className="text-[10px] font-bold uppercase tracking-[0.16em]"
              style={{ color: accent }}
              data-testid="flashcards-card-side"
            >
              Question
            </span>
            <span className="text-[11px] font-semibold text-slate-400 tabular-nums">
              {position} / {poolSize}
            </span>
          </div>
          <div className="flex-1 px-8 py-6 flex items-center justify-center text-center">
            <p className="text-xl sm:text-2xl font-semibold text-slate-900 leading-snug">
              {card.front}
            </p>
          </div>
          <div className="px-5 pb-4 text-[11px] text-slate-400 text-center">
            Tap to reveal the answer
          </div>
        </div>
        {/* back */}
        <div
          className="absolute inset-0 rounded-2xl border shadow-md flex flex-col items-stretch [backface-visibility:hidden]"
          style={{
            transform: "rotateY(180deg)",
            background: `linear-gradient(180deg, ${accent}12, #ffffff 40%)`,
            borderColor: accent,
          }}
          aria-hidden={!flipped}
        >
          <div className="flex items-center justify-between px-5 pt-4">
            <span
              className="text-[10px] font-bold uppercase tracking-[0.16em]"
              style={{ color: accent }}
            >
              Answer
            </span>
            <span className="text-[11px] font-semibold text-slate-400 tabular-nums">
              {position} / {poolSize}
            </span>
          </div>
          <div className="flex-1 px-6 py-5 overflow-y-auto text-left">
            <p className="text-base sm:text-lg text-slate-800 leading-relaxed">{card.back}</p>
          </div>
          <div className="px-5 pb-4 text-[11px] text-slate-500 text-center">
            Choose <b>Got it</b> or <b>Study again</b>
          </div>
        </div>
      </button>
    </div>
  );
}

function DeckClearedView({ category, onRestart }) {
  return (
    <div
      data-testid="flashcards-deck-cleared"
      className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-8 sm:p-10 text-center"
    >
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center">
        <Check className="w-7 h-7" />
      </div>
      <h2 className="mt-4 text-2xl font-bold text-emerald-900">Deck cleared</h2>
      <p className="mt-2 text-emerald-800 max-w-lg mx-auto leading-relaxed">
        You've marked every card in <b>{category.title}</b> as known. Come back tomorrow for a
        spaced-repetition refresh, or restart the deck now to review it again.
      </p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onRestart}
          data-testid="flashcards-cleared-restart"
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500"
        >
          <RotateCcw className="w-4 h-4" /> Restart deck
        </button>
        <Link
          to="/flashcards"
          data-testid="flashcards-cleared-back"
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-white px-5 py-2.5 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"
        >
          <ArrowRight className="w-4 h-4" /> Pick another deck
        </Link>
      </div>
    </div>
  );
}
